/**
 * PhoneVerify — the WhatsApp code that proves my mobile number at signup
 * (#431). Signup stays cardless; this is the only check. I type the number
 * (a local 0XX number becomes +27), send the code, type the 6 digits and
 * verify. The server's own message is shown on any refusal, and Resend
 * unlocks 30 seconds after a send.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useState } from 'react';
import { api } from '../api.js';

/** 082 123 4567 → +27821234567; an international number is left as typed. */
export function toSAInternational(raw) {
  const s = String(raw || '').replace(/[\s()-]/g, '');
  if (/^0\d{9}$/.test(s)) return `+27${s.slice(1)}`;
  if (/^27\d{9}$/.test(s)) return `+${s}`;
  return s;
}

/** @param {{ initialPhone?: string, onVerified: (phone: string) => void }} props */
export default function PhoneVerify({ initialPhone, onVerified }) {
  const [phone, setPhone] = useState(initialPhone || '+27');
  const [sentTo, setSentTo] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const send = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await api('/auth/phone/send-code/', { method: 'POST', body: { phone: toSAInternational(phone) } });
      setSentTo(r.phone); setWait(30);
      setMsg(`Code sent on WhatsApp to ${r.phone}.`);
    } catch (e) {
      setMsg(e.data?.detail || 'The code could not be sent — try again.');
    } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await api('/auth/phone/verify/', { method: 'POST', body: { phone: sentTo, code } });
      onVerified?.(r.phone);
    } catch (e) {
      setMsg(e.data?.detail || 'That code did not work — try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="card mt-4" data-guide="phone-verify">
      <h3 className="font-semibold">Verify your WhatsApp number</h3>
      <p className="text-sm text-slate-400 mt-1">I send a 6-digit code on WhatsApp. No card is needed to start.</p>
      <div className="mt-3 flex gap-2">
        <input className="input flex-1" inputMode="tel" value={phone}
               onChange={(e) => setPhone(e.target.value)} placeholder="+27 82 123 4567" />
        <button className="border border-edge rounded-xl px-4 disabled:opacity-40"
                disabled={busy || wait > 0} onClick={send}>
          {sentTo ? (wait > 0 ? `Resend in ${wait}s` : 'Resend') : 'Send code'}
        </button>
      </div>
      {sentTo && (
        <div className="mt-3 flex gap-2">
          <input className="input flex-1 tracking-[0.3em]" inputMode="numeric" maxLength={6}
                 value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="123456" />
          <button className="btn-primary px-4 disabled:opacity-40" disabled={busy || code.length !== 6}
                  onClick={verify}>Verify</button>
        </div>
      )}
      {msg && <p className="text-sm text-slate-400 mt-2">{msg}</p>}
    </div>
  );
}
